import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { ArrowLeft, Calendar, Tag as TagIcon, User } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import SEO, { articleJsonLd, breadcrumbJsonLd } from "@/components/SEO";
import NewsletterForm from "@/components/NewsletterForm";
import ShareBar from "@/components/ShareBar";
import api from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | 404

  useEffect(() => {
    setState("loading");
    api.get(`/blog/${slug}`)
      .then((r) => { setPost(r.data); setState("ok"); })
      .catch((e) => setState(e.response?.status === 404 ? "404" : "error"));
  }, [slug]);

  if (state === "loading") {
    return <SiteLayout branch="main"><section className="section"><p className="text-[var(--muted)]">Loading…</p></section></SiteLayout>;
  }
  if (state === "404") {
    return (
      <SiteLayout branch="main">
        <section className="section max-w-xl mx-auto text-center">
          <h1 className="font-display text-4xl text-[var(--navy)]">Post not found</h1>
          <p className="text-[var(--muted)] mt-4">This insight may have been unpublished or the link is wrong.</p>
          <Link to="/blog" className="btn btn-navy mt-8 inline-flex">Back to Insights</Link>
        </section>
      </SiteLayout>
    );
  }

  const date = post.published_at
    ? new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "";

  return (
    <SiteLayout branch="main">
      <SEO
        title={post.seo_title || `${post.title} — Catalyst BH`}
        path={`/blog/${slug}`}
        description={post.seo_description || post.excerpt}
        image={post.hero_image_url}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            breadcrumbJsonLd([["Home", "/"], ["Insights", "/blog"], [post.title]]),
            articleJsonLd({
              title: post.title,
              description: post.seo_description || post.excerpt,
              slug,
              image: post.hero_image_url,
              publishedAt: post.published_at,
              updatedAt: post.updated_at || post.published_at,
              author: post.author || "Catalyst BH Consulting",
            }),
          ],
        }}
      />
      <article className="pb-24">
        {post.hero_image_url && (
          <div className="w-full aspect-[16/6] bg-[var(--cream)] overflow-hidden">
            <img src={post.hero_image_url} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="section max-w-3xl mx-auto">
          <button
            onClick={() => navigate("/blog")}
            className="text-xs text-[var(--muted)] hover:text-[var(--navy)] flex items-center gap-1.5 mb-8"
            data-testid="blog-back">
            <ArrowLeft size={13} /> All insights
          </button>

          <p className="text-[11px] uppercase tracking-widest text-[var(--gold)]" data-testid="blog-post-category">
            {post.category || "Insights"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl text-[var(--navy)] mt-3 leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-lg text-[var(--muted)] mt-5 leading-relaxed">{post.excerpt}</p>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-[var(--muted)]">
            {date && (<span className="flex items-center gap-1.5"><Calendar size={12} /> {date}</span>)}
            {post.author && (<span className="flex items-center gap-1.5"><User size={12} /> {post.author}</span>)}
          </div>

          <ShareBar
            title={post.title}
            description={post.excerpt || ""}
            url={`https://catalystbhconsulting.com/blog/${slug}`}
            className="mt-6"
          />

          <div className="prose prose-neutral max-w-none mt-10
                          prose-headings:font-display prose-headings:text-[var(--navy)]
                          prose-a:text-[var(--navy)] prose-a:underline
                          prose-strong:text-[var(--navy)]
                          prose-blockquote:border-[var(--gold)] prose-blockquote:text-[var(--muted)]"
               data-testid="blog-post-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[
                rehypeRaw,
                // Allow common HTML tags AutoSEO / WordPress-style CMSes emit,
                // while sanitizing scripts, event handlers, iframes.
                [rehypeSanitize, {
                  ...defaultSchema,
                  tagNames: [
                    ...(defaultSchema.tagNames || []),
                    "figure", "figcaption", "picture", "source", "video", "audio", "u", "mark",
                  ],
                  attributes: {
                    ...defaultSchema.attributes,
                    "*": [...(defaultSchema.attributes?.["*"] || []), "className", "id", "style"],
                    img: [...(defaultSchema.attributes?.img || []), "loading", "width", "height", "srcset"],
                    a: [...(defaultSchema.attributes?.a || []), "target", "rel"],
                  },
                }],
              ]}
            >{post.body_md}</ReactMarkdown>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 pt-8 border-t border-[var(--line)] flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="text-xs px-3 py-1 border border-[var(--line)] hover:border-[var(--navy)] flex items-center gap-1.5"
                  data-testid={`blog-post-tag-${t}`}>
                  <TagIcon size={11} /> {t}
                </Link>
              ))}
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-[var(--line)]">
            <ShareBar
              title={post.title}
              description={post.excerpt || ""}
              url={`https://catalystbhconsulting.com/blog/${slug}`}
            />
          </div>

          <div className="mt-16">
            <NewsletterForm variant="hero" source={`blog:${slug}`} />
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}
